import axios from 'axios'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT, ENTITY_MANAGER_END_POINTS } from '../utils/environment'
import {
  MaxRteDiscoveryPayload,
  MaxRTEORGType,
  MaxRteTransactionPayload,
  PatientExperian,
  ShouldCallMaxRTEType,
} from '../types'
import { validateApiParams } from '../utils/functions'

const LAYOUT_ENDPOINT = `${API_END_POINT.LAYOUT_MANAGER}layout`

export interface FileUploadForPreSignedUrl {
  name?: string
  entity_id?: number
  content_type?: string
  size?: number
  field_id?: number
  type?: string
}

export interface TriggerConsentRequestPayload {
  entity_type: string
  entity_record_id: number
  program_id: number
  recipient_type: string
  email_template_id: number
  recipient_email: string
}

export const getAllLayoutApi = async <
  T extends {
    entityId: string | undefined
    sort?: string
    defaultValue?: boolean
    type?: string
  }
>(
  payload: T
) => {
  const params = {
    entityId: payload.entityId,
    sort: payload?.sort,
    size: 50000,
    defaultValue: payload.defaultValue,
    type: payload?.type,
  }
  validateApiParams(
    { entityId: payload.entityId },
    { endpoint: `${LAYOUT_ENDPOINT}/paginated`, method: 'GET' }
  )
  return await axiosInstance.get(`${LAYOUT_ENDPOINT}/paginated`, { params })
}
export const getLayoutByName = async <
  T extends {
    entityId: string | undefined
    layoutName: string | undefined
    sort?: string
  }
>(
  payload: T
) => {
  const params = {
    entityId: payload.entityId,
  }
  validateApiParams(
    { entityId: payload.entityId, layoutName: payload.layoutName },
    {
      endpoint: `${LAYOUT_ENDPOINT}/by-name/${payload.layoutName}`,
      method: 'GET',
    }
  )
  return await axiosInstance.get(
    `${LAYOUT_ENDPOINT}/by-name/${payload.layoutName}`,
    { params }
  )
}
export const getAllLayoutV2Api = async <
  T extends { entityId: string | undefined }
>(
  payload: T
) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}layout`
  const params = { entityId: payload.entityId }
  validateApiParams(
    { entityId: payload.entityId },
    { endpoint: url, method: 'GET' }
  )
  return await axiosInstance.get(url, { params })
}

export const updateDefaultLayouts = async (
  payload: { id: number; entity_id: number; type: string }[]
) => {
  const url = `${API_END_POINT.V3_ENTITY_MANAGER}layout/update-defaults`
  return await axiosInstance.patch(url, payload)
}

export const saveLayoutApi = async <T>(payload: T) => {
  return await axiosInstance.post(LAYOUT_ENDPOINT, payload)
}

export const getLayoutByIdApi = async <
  T extends { entityId: string | undefined; layoutId: string | undefined }
>(
  payload: T
) => {
  const url = `${LAYOUT_ENDPOINT}/${payload?.layoutId}`
  const params = { entityId: payload.entityId }
  validateApiParams(
    { ...params, layoutId: payload?.layoutId },
    {
      endpoint: url,
      method: 'GET',
    }
  )
  return await axiosInstance.get(url, { params })
}

export const getLayoutByEntityIdAndTypeApi = async <
  T extends {
    entityId: string | undefined
    type: string | undefined
    isDefault?: boolean
  }
>(
  payload: T
) => {
  const url = `${LAYOUT_ENDPOINT}?exclude=draft`
  const params = {
    entityId: payload?.entityId,
    type: payload?.type,
    ...(payload?.isDefault !== undefined && {
      defaultValue: !!payload?.isDefault,
    }),
  }
  validateApiParams(params, {
    endpoint: url,
    method: 'GET',
  })
  return await axiosInstance.get(url, { params })
}

export const getLayoutByEntityNameAndTypeApi = async <
  T extends {
    entityName: string | undefined
    type: string | undefined
    isDefault?: boolean
  }
>(
  payload: T
) => {
  const url = `${LAYOUT_ENDPOINT}?exclude=draft`
  const params = {
    entityName: payload?.entityName,
    type: payload?.type,
    defaultValue: !!payload?.isDefault,
  }
  validateApiParams(params, {
    endpoint: url,
    method: 'GET',
  })

  return await axiosInstance.get(url, { params })
}

export const updateLayoutByIdApi = async <
  T,
  U extends { layoutId: string | undefined; layout: T }
>(
  payload: U
) => {
  const url = `${LAYOUT_ENDPOINT}/${payload?.layoutId}`
  validateApiParams(
    {
      layoutId: payload?.layoutId as string,
    },
    {
      endpoint: url,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(url, payload?.layout)
}

export const deleteLayoutByIdApi = async <T>(layoutId: T) => {
  validateApiParams(
    {
      layout_id: layoutId as string,
    },
    {
      endpoint: `${LAYOUT_ENDPOINT}/${layoutId}`,
      method: 'DELETE',
    }
  )
  return axiosInstance.delete(`${LAYOUT_ENDPOINT}/${layoutId}`)
}

export const generatePresignedUrlCodeUpload = async (
  selectedFileName: string
) => {
  validateApiParams(
    { selectedFileName },
    {
      endpoint: `${API_END_POINT.CORE}v1/files/generate-presigned-url/PUBLIC?fileName=${selectedFileName}`,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(
    `${API_END_POINT.CORE}v1/files/generate-presigned-url/PUBLIC?fileName=${selectedFileName}`
  )
}

export const updatePresignedUrlCodeUpload = async (
  presignedUrl: string,
  selectedFile: File,
  selectedTenantId: number | undefined
) => {
  return await axios.put(presignedUrl, selectedFile, {
    headers: {
      'Content-Type': selectedFile.type,
      'x-tenantid': selectedTenantId,
    },
  })
}

export const fetchPresignedURLMultiUpload = async (
  payload: FileUploadForPreSignedUrl[],
  documentId?: number | null
) => {
  return await axiosInstance.post(
    `${
      API_END_POINT.ENTITY_MANAGER
    }entities/files/generate-upload-presigned-url${
      documentId ? `?id=${documentId}` : ''
    }`,
    payload
  )
}

export const presignedURLSingleUploadPutCall = async (
  presignedUrl: string,
  selectedFile: File,
  tagging_field?: string | undefined,
  documentIdentifierMetaData?: string | undefined
) => {
  return await axios.put(presignedUrl, selectedFile, {
    headers: {
      'Content-Type': selectedFile?.type,
      ...(tagging_field ? { 'x-amz-tagging': tagging_field } : {}),
      ...(documentIdentifierMetaData
        ? { 'x-amz-meta-DOCUMENT_IDENTIFIER': documentIdentifierMetaData }
        : {}),
    },
  })
}

export const fetchDownloadUrlForSingleFIles = async (
  rootPaths: string,
  file_name: string
) => {
  const normalizedRootPath = rootPaths.endsWith('/')
    ? rootPaths
    : rootPaths + '/'
  return await axiosInstance.get(
    `${
      API_END_POINT.ENTITY_MANAGER
    }entities/files/generate-download-presigned-url?rootPath=${
      normalizedRootPath + file_name
    }&file_name=${file_name}`
  )
}
export const fetchUnauthenticatedDownloadUrlForSingleFIles = async (
  rootPaths: string,
  file_name: string,
  tenantId: number,
  portalId: number
) => {
  const normalizedRootPath = rootPaths.endsWith('/')
    ? rootPaths
    : rootPaths + '/'
  return await axios.get(
    `${
      API_END_POINT.CORE
    }public/hcp/microsite/generate-download-presigned-url?rootPath=${
      normalizedRootPath + file_name
    }&file_name=${file_name}&portalId=${portalId}`,
    {
      headers: {
        'x-tenantid': tenantId,
      },
    }
  )
}

export const triggerConsentRequestApi = async (
  payload: TriggerConsentRequestPayload
) => {
  const url = `${API_END_POINT.CONSENT}/consent/requests`
  return await axiosInstance.post(url, payload)
}

export const maxRteDiscoveryApi = async (
  payload: MaxRteDiscoveryPayload,
  clientName: string
) => {
  const url = `${API_END_POINT.CORE}v1/integrations/${clientName}-maxrte-discovery`
  return await axiosInstance.post(url, payload)
}

export const maxRteTransactionApi = async (
  payload: MaxRteTransactionPayload,
  clientName: string
) => {
  const url = `${API_END_POINT.CORE}v1/integrations/${clientName}-maxrte-transaction`
  return await axiosInstance.post(url, payload)
}

export const getOrgIdMaxRte = async (
  patientState: string,
  productName: string
) => {
  return await axiosInstance.get<MaxRTEORGType>(
    `${API_END_POINT.CORE}v1/integrations/org-mapping?patientState=${patientState}&productId=${productName}`
  )
}

export const shouldCallMaxRTE = async (
  patientId: number,
  productId?: number | string,
  caseId?: number | string
) => {
  return await axiosInstance.get<ShouldCallMaxRTEType>(
    `${API_END_POINT.CORE}v1/integrations/validate`,
    {
      params: {
        patientId,
        ...(productId ? { productId } : {}),
        ...(caseId ? { caseId } : {}),
      },
    }
  )
}

export const getQueueEntity = async (
  tenant: number | null,
  page: number,
  limit: number
) => {
  const url = `${API_END_POINT.CORE}v1/search/get-data`
  return await axiosInstance.post(
    url,
    {
      entity_name: 'QueueEntity',
      select: ['id', 'name'],
      page: page,
      related_entities: {
        entity: {
          select: ['id'],
        },
        layout: {
          select: ['id'],
        },
      },

      size: limit ?? 10,
      filters: [
        {
          criteria: [
            {
              field_name: 'queue_type',
              values: ['USER'],
              operation: 'CONTAINS',
            },
            {
              field_name: 'deleted',
              values: ['false'],
              operation: 'CONTAINS',
            },
            {
              field_name: 'enabled',
              values: ['true'],
              operation: 'CONTAINS',
            },
            {
              field_name: 'status',
              values: ['Active'],
              operation: 'CONTAINS',
            },
            {
              field_name: 'name',
              direction: 'ASC',
            },
          ],
        },
      ],
    },
    {
      params: {
        tenant,
      },
    }
  )
}

export const experianFinancialClearance = async (payload: PatientExperian) => {
  const url = `${API_END_POINT.CORE}v1/integrations/validate/experian-financial-clearance`
  return await axiosInstance.post(url, payload)
}

export const getOptionsOnFocus = async (
  payload: {
    page: number
    size: number
    targetEntityId?: number
    fieldName?: string
  },
  dataId?: string
) => {
  const baseUrl = `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/filters/patients_view`

  const url = dataId ? `${baseUrl}?recordId=${dataId}` : baseUrl

  return await axiosInstance.post(url, payload)
}
export const fetchHybridLayoutForService = async (
  caseId: string,
  layoutType: string
) => {
  const baseUrl = `${API_END_POINT.V3_ENTITY_MANAGER}${ENTITY_MANAGER_END_POINTS.LAYOUT}/case_type/fetch/${caseId}`

  return await axiosInstance.get(baseUrl, {
    params: {
      layout_type: layoutType,
    },
  })
}
